import {Injectable} from '@angular/core';
import {BehaviorSubject, lastValueFrom, Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {SettingsApiService} from './api/settings-api.service';
import {jwtDecode} from 'jwt-decode';
import {ICbLogos, IMyDomains} from '../common/models/interfaces';
import {Store} from '@ngrx/store';
import {IButtonSettingsState} from '../common/ngrx-store/state/button-settings.state';
import {SetSettingsServices} from '../common/ngrx-store/action/user-services.action';
import {StripeHelperService} from './stripe-helper.service';
import {SetPaymentMethod} from '../common/ngrx-store/action/payment-method.action';
import {SetGlobalLoading} from '../common/ngrx-store/action/global-loading.action';
import {GetServicesService} from './get-services.service';
import {UserInfoManager} from '../common/elf-store/user-info/user-info.manager';
import {UserInfoState, UserStatus} from '../common/elf-store/user-info/user-info.type';
import {initialSettingsServicesState} from '../common/ngrx-store/reducers/user-services.reducer';
import {FormGroup} from '@angular/forms';
import {createInitialState} from '../common/elf-store/user-info/user-info.store';
import {UserApiService} from './api/users-api.service';
import {SsoAbstractResponseDtoExtendedJWTUserDetailsDto} from '../../userway/sdk/SSOApiClient';
import {ResetPassword1Data} from '../../userway/sdk/SSOApiClient';
import {IntercomUserDetailsListData} from '../../userway/sdk/UserWayApiClient';
import {WelcomeModalComponent} from '../components/dumb/welcome-modal/welcome-modal.component';
import {MatDialog} from '@angular/material/dialog';

const errorMessagesList = {
  password: {
    required: 'Password cannot be empty.',
    password: 'Please check your password',
    minlength: 'Password must be at least 6 characters.',
    incorrect: 'New password should not match the current one.',
    notSame: 'Passwords do not match. Please try again.',
  },
  email: {
    required: 'Email cannot be empty',
    email: 'Email must be a valid email address',
    same: 'Email must not match current email',
    notSame: 'Emails do not match. Please try again.',
    emailIsExist: 'User with this email already exists',
  },
};

export interface InfoWidgetResponse {
  data: {
    user: UserInfoState;
    jwtUserDetails: {
      ssoUserId: string;
      loginMechanism: string;
      targetSsoUserId: string;
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  myDomains = new BehaviorSubject<IMyDomains>({domains: []});
  myLogos = new BehaviorSubject<ICbLogos>({logos: []});
  newUserSiteId = null;
  shouldShowWelcomePopup = true;

  emailFromLogin = new BehaviorSubject<string>('');
  passwordPassShow: {[key: string]: boolean} = {
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
    changeEmailPassword: false,
  };

  constructor(
    private store: Store<IButtonSettingsState>,
    private userInfoManager: UserInfoManager,
    private getServicesService: GetServicesService,
    private settingsApiService: SettingsApiService,
    private stripeService: StripeHelperService,
    private userApiService: UserApiService,
    private dialog: MatDialog,
  ) {}

  clearUserInfo() {
    this.userInfoManager.setUserInfo(createInitialState());
  }

  getUserInfo(skipSavingUserData: boolean = false): Observable<InfoWidgetResponse> {
    const data = this.getInfoFromJWT();
    const accountCode = data?.targetAccountCode;

    return this.userApiService.infoWidget(accountCode).pipe(
      tap(info => {
        if (!skipSavingUserData) {
          const userInfo: UserInfoState = {
            accountCode: data?.targetAccountCode,
            firstName: data?.firstName,
            lastName: data?.lastName,
            username: data?.username,
            ...info?.data?.user,
            siteCreateOnTuningsEnabled: info?.data?.user?.siteCreateOnTunings?.enabled,
            userStatus: UserStatus.LoggedIn,
            regulationTrackerMode: info?.data?.user?.regulation_tracker_mode_only,
            trialInfo: {
              isTrialNoCardExpired: info?.data?.user?.trialInfo?.isTrialNoCardExpired,
              isTrialNoCardWelcomeScreenShow: info?.data?.user?.trialInfo?.isTrialNoCardWelcomeScreenShow,
              isTrialNoCard: info?.data?.user?.trialInfo?.isTrialNoCard,
            },
          };
          this.userInfoManager.setUserInfo(userInfo);

          // shouldShowWelcomePopup - to prevent opening dialog multiple times
          if (
            this.shouldShowWelcomePopup &&
            info.data.user.trialInfo?.isTrialNoCardWelcomeScreenShow &&
            info.data.user.trialInfo?.isTrialNoCard
          ) {
            this.dialog.open(WelcomeModalComponent);
            this.shouldShowWelcomePopup = false;
          }
        }
      }),
    );
  }

  getTeamOwnerInfo(code: string) {
    const data = this.getInfoFromJWT();
    return this.userApiService.infoWidget(code).pipe(
      tap(info => {
        const userInfo: UserInfoState = {
          firstName: data?.firstName,
          lastName: data?.lastName,
          username: data?.username,
          ...info?.data?.user,
          siteCreateOnTuningsEnabled: info?.data?.user?.siteCreateOnTunings?.enabled,
          userStatus: UserStatus.LoggedIn,
          ssoUserId: data?.ssoUserId,
          targetSsoUserId: info?.data?.jwtUserDetails?.targetSsoUserId,
          accountCode: info?.data?.user?.code,
          regulationTrackerMode: info?.data?.user?.regulation_tracker_mode_only,
          trialInfo: {
            isTrialNoCardExpired: info?.data?.user?.trialInfo?.isTrialNoCardExpired,
            isTrialNoCardWelcomeScreenShow: info?.data?.user?.trialInfo?.isTrialNoCardWelcomeScreenShow,
            isTrialNoCard: info?.data?.user?.trialInfo?.isTrialNoCard,
          },
        };
        this.userInfoManager.setUserInfo(userInfo);
      }),
    );
  }

  getInfoFromJWT(): any {
    try {
      const token = jwtDecode(localStorage.getItem('UserWayManageAppToken'));

      return JSON.parse(token['sub']);
    } catch (e) {
      return null;
    }
  }

  getDefaultPaymentMethod(): void {
    this.stripeService.getDefaultPaymentMethod().subscribe(response => {
      const data = {paymentMethod: response};
      this.store.dispatch(new SetPaymentMethod(data));
    });
  }

  getServiceData(siteId: number): void {
    this.getProductPaidDataAll(siteId).subscribe(response => {
      let data: any = this.prepareDataForSettingsServices(response);
      data = {...this.myDomains.value, ...data};
      data = {...this.myLogos.value, ...data};
      this.store.dispatch(new SetSettingsServices(data));
      this.store.dispatch(new SetGlobalLoading({loading: false}));
    });
  }

  prepareDataForSettingsServices(data: any): any {
    return {
      ['VISIBLE']: data.options.widgetVisible,
      ['CUSTOM_BRANDING']: {
        custom_logo_image_link: data.products['CUSTOM_BRANDING']?.addon?.custom_logo_image_link,
        custom_logo_image_path: data.products['CUSTOM_BRANDING']?.addon?.custom_logo_image_path,
        expires_at: data.products['CUSTOM_BRANDING']?.userService?.expires_at,
        id: data.products['CUSTOM_BRANDING']?.addon?.id,
        is_enabled: data.products['CUSTOM_BRANDING']?.addon?.is_enabled,
        is_renewable: data.products['CUSTOM_BRANDING']?.addon?.is_renewable,
        is_trial: data.products['CUSTOM_BRANDING']?.userService?.is_trial,
        trial_applicable:
          data.products['CUSTOM_BRANDING']?.meta?.trialApplicable && !data.products['CUSTOM_BRANDING']?.addon,
        user_service_id: data.products['CUSTOM_BRANDING']?.userService?.id,
        subscription: data.products['CUSTOM_BRANDING']?.subscription,
      },
      ['MODIFY_MENU']: {
        color_mode: data.products['MODIFY_MENU']?.addon?.color_mode,
        expires_at: data.products['MODIFY_MENU']?.userService?.expires_at,
        features_pattern:
          data.products['MODIFY_MENU']?.addon?.features_pattern || 's1|s9|s3|s6|s4|s14|s13|s7|s2|s12|s11|s23|s25',
        id: data.products['MODIFY_MENU']?.addon?.id,
        dictionary: data.products['MODIFY_MENU']?.addon?.dictionary,
        profiles: data.products['MODIFY_MENU']?.addon?.profiles,
        oversized: data.products['MODIFY_MENU']?.addon?.oversized,
        two_columns_layout: data.products['MODIFY_MENU']?.addon?.two_columns_layout,
        is_enabled: data.products['MODIFY_MENU']?.addon?.is_enabled,
        is_renewable: data.products['MODIFY_MENU']?.addon?.is_renewable,
        is_trial: data.products['MODIFY_MENU']?.userService?.is_trial,
        trial_applicable: data.products['MODIFY_MENU']?.meta?.trialApplicable && !data.products['MODIFY_MENU']?.addon,
        user_service_id: data.products['MODIFY_MENU']?.userService?.id,
        subscription: data.products['MODIFY_MENU']?.subscription,
      },
      ['REMEDIATION']: {
        expires_at: data.products['REMEDIATION']?.userService?.expires_at,
        fixes_headers_file_path: data.products['REMEDIATION']?.addon?.fixes_headers_file_path,
        fixes_img_alt_file_path: data.products['REMEDIATION']?.addon?.fixes_img_alt_file_path,
        fixes_social_sensitivity_file_path: data.products['REMEDIATION']?.addon?.fixes_social_sensitivity_file_path,
        fixes_vague_links_file_path: data.products['REMEDIATION']?.addon?.fixes_vague_links_file_path,
        id: data.products['REMEDIATION']?.addon?.id,
        img_ai_processed_number: data.products['REMEDIATION']?.addon?.img_ai_processed_number,
        is_enabled: data.products['REMEDIATION']?.addon?.is_enabled,
        is_renewable: data.products['REMEDIATION']?.addon?.is_renewable,
        is_trial: data.products['REMEDIATION']?.userService?.is_trial,
        numbers: data.products['REMEDIATION']?.addon?.numbers,
        trial_applicable: data.products['REMEDIATION']?.meta?.trialApplicable && !data.products['REMEDIATION']?.addon,
        user_service_id: data.products['REMEDIATION']?.userService?.id,
        subscription: data.products['REMEDIATION']?.subscription,
      },
      ['USAGE_STATS']: {
        ...data.products['USAGE_STATS']?.addon?.stats,
        expires_at: data.products['USAGE_STATS']?.userService?.expires_at,
        id: data.products['USAGE_STATS']?.addon?.id,
        is_enabled: data.products['USAGE_STATS']?.addon?.is_enabled,
        is_renewable: data.products['USAGE_STATS']?.addon?.is_renewable,
        is_trial: data.products['USAGE_STATS']?.userService?.is_trial,
        trial_applicable: data.products['USAGE_STATS']?.meta?.trialApplicable && !data.products['USAGE_STATS']?.addon,
        user_service_id: data.products['USAGE_STATS']?.userService?.id,
        subscription: data.products['USAGE_STATS']?.subscription,
      },
      ['WHITE_LABEL']: {
        expires_at: data.products['WHITE_LABEL']?.userService?.expires_at,
        hide_asterisk: data.products['WHITE_LABEL']?.addon?.hide_asterisk,
        hide_donate: data.products['WHITE_LABEL']?.addon?.hide_donate,
        hide_logo: data.products['WHITE_LABEL']?.addon?.hide_logo,
        hide_manage: data.products['WHITE_LABEL']?.addon?.hide_manage,
        hide_report: data.products['WHITE_LABEL']?.addon?.hide_report,
        id: data.products['WHITE_LABEL']?.addon?.id,
        is_enabled: data.products['WHITE_LABEL']?.addon?.is_enabled,
        is_renewable: data.products['WHITE_LABEL']?.addon?.is_renewable,
        is_trial: data.products['WHITE_LABEL']?.userService?.is_trial,
        trial_applicable: data.products['WHITE_LABEL']?.meta?.trialApplicable && !data.products['WHITE_LABEL']?.addon,
        user_service_id: data.products['WHITE_LABEL']?.userService?.id,
        subscription: data.products['WHITE_LABEL']?.subscription,
      },
      ['LIVE_TRANSLATIONS']: {
        auto_renew_on_quota_exceeded: data.products['LIVE_TRANSLATIONS']?.addon?.auto_renew_on_quota_exceeded,
        auto_translation: data.products['LIVE_TRANSLATIONS']?.addon?.auto_translation,
        expires_at: data.products['LIVE_TRANSLATIONS']?.userService?.expires_at,
        id: data.products['LIVE_TRANSLATIONS']?.addon?.id,
        is_enabled: data.products['LIVE_TRANSLATIONS']?.addon?.is_enabled,
        is_renewable: data.products['LIVE_TRANSLATIONS']?.addon?.is_renewable,
        is_trial: data.products['LIVE_TRANSLATIONS']?.userService?.is_trial,
        trial_applicable:
          data.products['LIVE_TRANSLATIONS']?.meta?.trialApplicable && !data.products['LIVE_TRANSLATIONS']?.addon,
        user_service_id: data.products['LIVE_TRANSLATIONS']?.userService?.id,
        pricing_tier: data.products['LIVE_TRANSLATIONS']?.addon?.pricing_tier,
        subscription: data.products['LIVE_TRANSLATIONS']?.subscription,
      },
    };
  }

  getWidgetStats(currentUrl: string) {
    return this.settingsApiService.getWidgetStats(currentUrl).pipe(
      map((response: any) => {
        return response.payload;
      }),
    );
  }

  getProductPaidDataAll(siteId: number) {
    return this.settingsApiService.getProductPaidDataAll(siteId).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  getProducts(siteId: number) {
    return this.settingsApiService.getProducts(siteId).pipe(
      map((response: any) => {
        return response;
      }),
    );
  }

  oauthExchange(code: string) {
    return this.settingsApiService.oauthExchange(code).pipe(
      map((response: any) => {
        return response.payload;
      }),
    );
  }

  oauthExchangeRecoveryCode(code: string) {
    return this.settingsApiService.oauthRecoveryCodeExchange(code).pipe(
      map((response: ResetPassword1Data) => {
        return response.payload;
      }),
    );
  }

  getExtendedTokenData() {
    return lastValueFrom(
      this.settingsApiService
        .getExtendedTokenData()
        .pipe(map((response: SsoAbstractResponseDtoExtendedJWTUserDetailsDto) => response.payload)),
    );
  }

  clearSettingServiceStore() {
    this.store.dispatch(new SetSettingsServices(initialSettingsServicesState));
  }

  getUserDataIntercom(): Observable<IntercomUserDetailsListData> {
    return this.settingsApiService.getUserDataIntercom();
  }

  getFormErrors(controlKey: string, changeForm: FormGroup) {
    const listOfErrors = Object.keys(changeForm.get(controlKey).errors);
    const formErrorMessages = [];
    listOfErrors.forEach(error => {
      formErrorMessages.push(errorMessagesList[controlKey.split('_')[0]][error]);
    });
    return formErrorMessages;
  }

  getDomains(siteId: number) {
    this.getServicesService.getMyDomains(siteId).subscribe(sites => {
      this.myDomains.next({domains: sites?.domains});
      if (sites?.domains.length) {
        this.getServiceData(siteId);
      }
    });
  }
}
